import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { SnackbarState, UploadLimits, UploadProgressState } from '../types';
import {
  calculateTotalSize,
  createZipFromFiles,
  filterCsvFiles,
  getFilesFromEntry,
  isZipFile,
} from '../utils/fileUpload';
import type { FileSystemEntry } from '../utils/fileUpload';

interface UseDropzoneUploadParams {
  remainingSlots: number;
  remainingUploadSize: number;
  uploadLimits?: UploadLimits;
  isUploading: boolean;
  uploadFile: (formData: FormData, startPercentage?: number) => void;
  setSnackbar: Dispatch<SetStateAction<SnackbarState>>;
}

export interface UseDropzoneUploadReturn {
  isDragging: boolean;
  isPreparing: boolean;
  preparationProgress: UploadProgressState;
  handleDragEnter: (event: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  handleDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (event: React.DragEvent<HTMLDivElement>) => Promise<void>;
  handleFolderSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFiles: (files: File[] | null, folderName?: string) => Promise<void>;
}

export const useDropzoneUpload = ({
  remainingSlots,
  remainingUploadSize,
  uploadLimits,
  isUploading,
  uploadFile,
  setSnackbar,
}: UseDropzoneUploadParams): UseDropzoneUploadReturn => {
  const [isDragging, setIsDragging] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [preparationProgress, setPreparationProgress] = useState<UploadProgressState>({
    percentage: 0,
    processedBytes: 0,
    totalBytes: 0,
  });

  const handleFiles = useCallback(async (files: File[] | null, folderName?: string) => {
    if (isUploading || isPreparing) {
      setSnackbar({ open: true, message: 'アップロード処理中です。完了後に再度お試しください。', severity: 'info' });
      return;
    }

    if (!uploadLimits) {
      setSnackbar({ open: true, message: 'アップロード制限を確認できませんでした。再試行してください。', severity: 'error' });
      return;
    }

    if (remainingSlots <= 0) {
      setSnackbar({ open: true, message: 'アップロード上限に達しています。', severity: 'error' });
      return;
    }

    if (!files || files.length === 0) {
      return;
    }

    const isSingleZipUpload = files.length === 1 && isZipFile(files[0]);
    const csvFiles = isSingleZipUpload ? [] : filterCsvFiles(files);
    const uploadableFiles = isSingleZipUpload ? files : csvFiles;

    if (uploadableFiles.length === 0) {
      setSnackbar({ open: true, message: '選択またはドロップされたアイテムにCSVファイルが見つかりませんでした。', severity: 'error' });
      return;
    }

    const totalSize = calculateTotalSize(uploadableFiles);
    if (totalSize > remainingUploadSize) {
      const remainingMb = Math.max(0, remainingUploadSize / (1024 * 1024));
      setSnackbar({
        open: true,
        message: `選択されたファイルの合計サイズが残りアップロード可能容量（${remainingMb.toFixed(2)}MB / 上限${uploadLimits.max_total_upload_size_mb}MB）を超えています。`,
        severity: 'error',
      });
      return;
    }

    const formData = new FormData();

    if (isSingleZipUpload) {
      formData.append('file', files[0], files[0].name);
    } else if (csvFiles.length === 1 && files.length === 1) {
      formData.append('file', csvFiles[0], csvFiles[0].name);
    } else {
      try {
        setIsPreparing(true);
        setPreparationProgress({ percentage: 0, processedBytes: 0, totalBytes: totalSize });
        const zipBlob = await createZipFromFiles(csvFiles, (percentage) => {
          setPreparationProgress({
            percentage: percentage * 0.1,
            processedBytes: Math.round(totalSize * percentage / 100),
            totalBytes: totalSize,
          });
        });
        formData.append('file', zipBlob, `${folderName || 'dropped_files'}.zip`);
      } catch (error) {
        console.error('Failed to create ZIP archive:', error);
        setSnackbar({ open: true, message: 'フォルダの圧縮に失敗しました。もう一度お試しください。', severity: 'error' });
        return;
      } finally {
        setIsPreparing(false);
      }
    }

    formData.append('filename', '');

    uploadFile(formData, isSingleZipUpload || csvFiles.length === 1 ? 0 : 10);
  }, [isUploading, isPreparing, remainingSlots, setSnackbar, remainingUploadSize, uploadLimits, uploadFile]);

  const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const items = event.dataTransfer.items;
    if (!items || items.length === 0) {
      return;
    }

    let folderName: string | undefined;
    const firstEntry = (items[0] as any)?.webkitGetAsEntry?.() as FileSystemEntry | undefined;
    if (firstEntry && 'isDirectory' in firstEntry && firstEntry.isDirectory) {
      folderName = firstEntry.name;
    }

    try {
      const promises = Array.from(items).map((item) => {
        const entry = (item as any)?.webkitGetAsEntry?.() as FileSystemEntry | undefined;
        return entry ? getFilesFromEntry(entry) : Promise.resolve([]);
      });
      const filesArrays = await Promise.all(promises);
      const allFiles = filesArrays.flat();
      await handleFiles(allFiles, folderName);
    } catch (error) {
      console.error('Error processing dropped files:', error);
      setSnackbar({ open: true, message: 'ファイルの処理中にエラーが発生しました。', severity: 'error' });
    }
  }, [handleFiles, setSnackbar]);

  const handleFolderSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList) {
      return;
    }

    const files = Array.from(fileList);
    const folderName = files.length > 0 && files[0].webkitRelativePath
      ? files[0].webkitRelativePath.split('/')[0]
      : 'selected_folder';

    void handleFiles(files, folderName);
    event.target.value = '';
  }, [handleFiles]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList) {
      return;
    }

    const files = Array.from(fileList);
    void handleFiles(files);
    event.target.value = '';
  }, [handleFiles]);

  return {
    isDragging,
    isPreparing,
    preparationProgress,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFolderSelect,
    handleFileSelect,
    handleFiles,
  };
};
